import { localSourceGraphPreparedSchema, localCompiledPackageCommandSchema, LOCAL_COMPILED_PACKAGE_PATH,
  LOCAL_SOURCE_GRAPH_PATH, LOCAL_MACRO_GRAPH_PATH, LOCAL_EDITOR_PORTS, type AnalysisChunkDescriptor, type AnalysisProgress,
  type MacroPackageRunReply } from '@playcastdotio/Messaging';
import type { LocalVisualTestAnalysisRunner } from '../../services/playjector/managers/LocalVisualTestAnalysisRunner';
import type { NativeMacroPackageBridge } from '../../services/playjector/NativeMacroPackageBridge';
import { ZodError } from 'zod';
import { LocalPackageRunHistory } from './LocalPackageRunHistory';

export const LOCAL_GRAPH_CALIBRATE_ACTION = 'macroLocalGraphCalibrate';
export const LOCAL_PACKAGE_ACTION = 'macroLocalPackage';
type Reply = (action: string, message: unknown) => void;

/** Only the owning local control session reaches this router. Native package paths are never browser-authored. */
export class LocalSourceGraphActionRouter {
  private readonly runs = new LocalPackageRunHistory();
  private generation = 0;
  private calibrationId: string | null = null;
  private calibrationEpoch = 0;
  public constructor(private readonly analysis: Pick<LocalVisualTestAnalysisRunner, 'run' | 'cancel'>,
    private readonly native: Pick<NativeMacroPackageBridge, 'start' | 'validate' | 'status' | 'cancel' | 'onReply' | 'onDisconnected'>
      & Partial<Pick<NativeMacroPackageBridge, 'setPreviewSensitivity'>>,
    private readonly diagnostic: (event: string, fields: Record<string, unknown>) => void = console.error) {
    native.onReply(reply => this.runs.observe(reply));
    native.onDisconnected(() => this.release());
  }

  public dispatch(action: string, payload: Record<string, unknown>, reply: Reply, progress: (message: unknown) => void): boolean {
    if (action !== LOCAL_GRAPH_CALIBRATE_ACTION && action !== LOCAL_PACKAGE_ACTION) return false;
    const requestId = String(payload.requestId ?? '');
    const operation = action === LOCAL_GRAPH_CALIBRATE_ACTION
      ? 'cancelAnalysisId' in payload ? this.cancelCalibration(payload.cancelAnalysisId)
        : this.calibrate(payload, item => progress({ requestId, progress: item })) : this.packageCommand(payload);
    void operation.then(result => reply(action, { requestId, success: true, ...result }))
      .catch(error => {
        const message = error instanceof Error ? error.message : 'local_graph_request_failed';
        const bounded = message.length <= 128 && /^[a-z0-9_]+$/.test(message) ? message : 'local_graph_response_invalid';
        try { this.diagnostic('local_graph_action_failed', { action, requestId, errorCode: bounded,
          ...(error instanceof ZodError ? { schemaIssues: error.issues.slice(0, 8).map(issue => ({
            path: issue.path.join('.').slice(0, 256), code: issue.code, message: issue.message.slice(0, 256),
          })) } : {}) }); } catch { /* Diagnostics cannot interrupt the response. */ }
        reply(action, { requestId, success: false, error: bounded });
      });
    return true;
  }
  public release(): void {
    this.generation++;
    this.calibrationId = null;
    this.calibrationEpoch++;
    this.analysis.cancel();
    const active = this.runs.active();
    this.runs.clear();
    for (const runId of active) void this.native.cancel(runId).catch(() => undefined);
  }
  private async calibrate(payload: Record<string, unknown>, progress: (item: AnalysisProgress) => void) {
    const prepared = localSourceGraphPreparedSchema.parse(payload.prepared);
    const base = [LOCAL_SOURCE_GRAPH_PATH, LOCAL_MACRO_GRAPH_PATH].map(root => `${root}/analyses/${prepared.analysisId}`)
      .find(root => new URL(prepared.assetGrantEndpoint).pathname === root + '/grants');
    if (!base) throw new Error('local_graph_endpoint_invalid');
    this.trusted(prepared.assetGrantEndpoint, base + '/grants');
    this.trusted(prepared.manifest.url, base + '/objects/');
    if (prepared.expiresAtUnixMs <= Date.now()) throw new Error('local_graph_analysis_expired');
    const chunks = new Map<number, AnalysisChunkDescriptor>();
    if (this.calibrationId) throw new Error('local_graph_calibration_busy');
    this.calibrationId = prepared.analysisId;
    const epoch = ++this.calibrationEpoch;
    try {
      const { campaignId, trigger, variant, rev } = prepared.sourceIdentity;
      const completion = await this.analysis.run({ testId: prepared.analysisId, manifest: prepared.manifest,
        manifestDigest: prepared.manifestDigest, workPlanDigest: prepared.workPlanDigest,
        preparation: { target: { campaignId, trigger, variant, rev }, expectedBindings: prepared.expectedBindings,
          casesTotal: prepared.expectedComparisons },
        assetGrantEndpoint: prepared.assetGrantEndpoint, assetToken: prepared.assetToken }, progress, chunk => chunks.set(chunk.sequence, chunk));
      if (this.calibrationEpoch !== epoch) throw new Error('local_graph_calibration_cancelled');
      return { completion, chunks: [...chunks.values()].sort((a, b) => a.sequence - b.sequence) };
    } finally { if (this.calibrationEpoch === epoch) this.calibrationId = null; }
  }
  private async cancelCalibration(analysisId: unknown): Promise<{ cancelled: boolean }> {
    if (typeof analysisId !== 'string' || analysisId !== this.calibrationId) throw new Error('local_graph_analysis_not_owned');
    this.calibrationId = null; this.calibrationEpoch++; this.analysis.cancel(); return { cancelled: true };
  }
  private async packageCommand(payload: Record<string, unknown>): Promise<{ reply: MacroPackageRunReply }> {
    const command = localCompiledPackageCommandSchema.parse(payload.operation);
    if (command.command === 'activate' || command.command === 'prepare'
      || (command.command === 'start' && (command.liveDetection !== 'continuous' || command.mode !== 'local-shadow'))) throw new Error('macro_graph_migration_required');
    if (command.command === 'status' || command.command === 'cancel' || command.command === 'preview-sensitivity') {
      if (!this.runs.owns(command.runId)) throw new Error('local_package_run_not_owned');
      const known = this.runs.terminal(command.runId);
      if (known) return { reply: known };
      const generation = this.generation;
      const reply = command.command === 'status' ? await this.native.status(command.runId)
        : command.command === 'cancel' ? await this.native.cancel(command.runId)
          : await this.previewSensitivity(command);
      if (generation !== this.generation) throw new Error('local_package_session_released');
      // A terminal event can arrive while Status is in flight, before its stale run_not_found reply.
      const terminal = this.runs.terminal(command.runId);
      if (terminal) return { reply: terminal };
      this.runs.acknowledge(reply);
      this.succeeded(reply); return { reply };
    }
    if (command.package.source !== 'grant' || command.package.packageKey !== `local-compiled/${command.packageSha256}`) {
      throw new Error('local_package_reference_invalid');
    }
    this.trusted(command.package.grant.grantEndpoint, `${LOCAL_COMPILED_PACKAGE_PATH}/grants/`);
    if (command.package.grant.expiresAtUnixMs <= Date.now()) throw new Error('local_package_grant_expired');
    return this.admit(command);
  }
  private async admit(command: Extract<ReturnType<typeof localCompiledPackageCommandSchema.parse>, { command?: 'start' | 'validate' }>): Promise<{ reply: MacroPackageRunReply }> {
    const runId = crypto.randomUUID(), generation = this.generation;
    if (command.command !== 'validate') this.runs.claim(runId, command.packageSha256);
    let reply: MacroPackageRunReply;
    try {
      reply = command.command === 'validate'
        ? await this.native.validate({ runId, captureTarget: 'selected', package: command.package, packageSha256: command.packageSha256,
          expectedHost: command.expectedHost, validationContextDigest: command.validationContextDigest })
        : await this.native.start({ runId, mode: command.mode ?? 'local-shadow',
          ...(command.liveDetection ? { liveDetection: command.liveDetection } : {}),
          ...(command.previewSettings ? { previewSettings: command.previewSettings } : {}),
          ...(command.matchingViewport ? { matchingViewport: command.matchingViewport } : {}),
          captureTarget: 'selected', package: command.package, packageSha256: command.packageSha256 });
    } catch (error) { this.runs.forget(runId); throw error; }
    if (generation !== this.generation) {
      if (command.command !== 'validate') await this.native.cancel(runId).catch(() => undefined);
      this.runs.forget(runId);
      throw new Error('local_package_session_released');
    }
    if (command.command !== 'validate') {
      this.runs.admitted(runId, reply);
      reply = this.runs.terminal(runId) ?? reply;
      if (reply.outcome === 'failed') this.runs.forget(runId);
    }
    this.succeeded(reply);
    if (command.command === 'validate' && reply.outcome !== 'validated') throw new Error('local_package_validation_failed');
    return { reply };
  }
  private previewSensitivity(command: { runId: string; sensitivity: number; rawScores: boolean }): Promise<MacroPackageRunReply> {
    if (!this.native.setPreviewSensitivity) throw new Error('local_package_preview_unsupported');
    return this.native.setPreviewSensitivity(command.runId, command.sensitivity, command.rawScores);
  }
  private succeeded(reply: MacroPackageRunReply): void {
    if (reply.outcome === 'failed') throw new Error(reply.errorCode ?? 'local_package_native_failed');
  }
  private trusted(input: string, prefix: string): void {
    const url = new URL(input);
    if (url.protocol !== 'http:' || url.hostname !== '127.0.0.1' || !LOCAL_EDITOR_PORTS.includes(Number(url.port) as 27130)
      || !url.pathname.startsWith(prefix) || url.username || url.password || url.search || url.hash) throw new Error('local_graph_endpoint_invalid');
  }
}
